import {
  basicLogger,
  initialize,
  LDClient,
  LDMultiKindContext,
  LDSingleKindContext,
} from 'launchdarkly-js-client-sdk';
import { config } from 'lib-shared-backend-config';
import {
  defaultFeatureFlags,
  FeatureFlags as FeatureFlagsT,
} from 'lib-shared-feature-flags';
import { getHighestUserRole } from 'lib-shared-user-role';

import { kebabToCamelCase } from 'utils/formatting/string';

import {
  FeatureFlagName,
  FeatureFlagResult,
  FeatureFlagsOrganization,
  FeatureFlagsUser,
  InitResolvedValue,
} from './featureFlags.types';

/**
 * Represents the feature flags setup.
 *
 * This encapsulates the logic and communication with LaunchDarkly as well as
 * feature flag evaluation and tracking.
 */
class FeatureFlags {
  /**
   * The evaluated flags.
   *
   * We keep track of this to avoid spamming LaunchDarkly with identical
   * evaluations of the same flag. Essentially we only evaluate once per
   * session
   *
   * - I.e. until the agent refreshes the page.
   */
  private readonly evaluated = new Set<string>();
  /** The LaunchDarkly client. */
  private client: LDClient | null = null;
  /** The feature flags. */
  private flags: FeatureFlagsT | null = null;

  /**
   * (Re)Initializes the LaunchDarkly client.
   *
   * @param user The user.
   * @param organization The organization.
   */
  async init(
    user: FeatureFlagsUser,
    organization: FeatureFlagsOrganization,
  ): Promise<InitResolvedValue> {
    this.deleteUnusedFlagsFromLocalStorage();

    return new Promise<InitResolvedValue>((resolve) => {
      if (this.client) {
        this.client.close().then(() => {
          this.setupClient(user, organization);
          this.setupClientEventListeners({ validate: false, resolve });
        });
      } else {
        this.setupClient(user, organization);
        this.setupClientEventListeners({ validate: true, resolve });
      }
    });
  }

  /**
   * Gets the feature flags.
   *
   * @param names The names.
   */
  get<TFeatureFlagName extends FeatureFlagName>(
    names: readonly TFeatureFlagName[],
  ): FeatureFlagResult<TFeatureFlagName> {
    if (!this.flags) {
      throw Error(
        `The feature flags cannot be used before they are initialized, ${names.join(
          ', ',
        )}`,
      );
    }

    this.track(names);
    return names.reduce(
      (acc, name) => ({
        ...acc,
        [kebabToCamelCase(name)]: this.flags?.[name],
      }),
      {} as FeatureFlagResult<TFeatureFlagName>,
    );
  }

  /**
   * Tracks the variations.
   *
   * @param names The names.
   */
  track<TFeatureFlagName extends FeatureFlagName>(
    names: readonly TFeatureFlagName[],
  ): void {
    names
      .filter((name) => !this.evaluated.has(name))
      .forEach((name) => {
        if (this.client) {
          this.client.variation(name);
          this.evaluated.add(name);
        }
      });
  }

  /**
   * Sets up the LaunchDarkly client.
   *
   * @param user The user.
   * @param organization The organization.
   */
  private setupClient(
    user: FeatureFlagsUser,
    organization: FeatureFlagsOrganization,
  ): void {
    const userContext: LDSingleKindContext = {
      kind: 'user',
      key: user.id,
      email: user.email ?? undefined,
      name: `${organization.name}: user`,
      role: getHighestUserRole(user.roles),

      /**
       * The following properties have been moved to the `organization` context,
       * and should be removed from the `user` context as soon as feature flag
       * targeting rules are migrated in LaunchDarkly.
       */
      organizationID: organization.id,
      organizationDomain: organization.subdomain,
      trialEndedAt: organization?.trial_ended_at
        ? organization?.trial_ended_at
        : '',
      isInTrial: typeof organization.trial_ended_at === 'string' ? false : true,

      /**
       * The following properties have been moved to the `developer` context,
       * and should be removed from the `user` context as soon as feature flag
       * targeting rules are migrated in LaunchDarkly.
       */
      appVersion: VERSION,
      appBuildTime: TIMESTAMP,
      hostname: window.location.hostname,

      _meta: {
        privateAttributes: ['email'],
      },
    };

    const organizationContext: LDSingleKindContext = {
      kind: 'organization',
      key: organization.id,
      name: organization.name,
      subdomain: organization.subdomain,
      trialEndedAt: organization?.trial_ended_at
        ? organization?.trial_ended_at
        : '',
      isInTrial: typeof organization.trial_ended_at === 'string' ? false : true,
    };

    const developerContext: LDSingleKindContext = {
      kind: 'developer',
      key: `developer:${user.id}`,
      appVersion: VERSION,
      appBuildTime: TIMESTAMP,
      hostname: window.location.hostname,
    };

    this.client = initialize(
      config.sdk.launchDarkly,
      {
        kind: 'multi',
        user: userContext,
        organization: organizationContext,
        developer: developerContext,
      },
      {
        bootstrap: 'localStorage',
        sendEvents: true,
        sendEventsOnlyForVariation: true,
        logger: basicLogger({ level: 'warn' }),
      },
    );
  }

  /*
   * Clear feature flags saved in local storage for old builds.
   */
  private deleteUnusedFlagsFromLocalStorage(): void {
    Object.keys(localStorage)
      .filter((key) => /ld:\w+:(?!\$)\w+/.test(key))
      .forEach((key) => {
        const [_ld, token, userEncodedB64] = key.split(':');
        const contexts: LDMultiKindContext = JSON.parse(atob(userEncodedB64));

        const { appVersion, appBuildTime } =
          (contexts.developer as LDSingleKindContext) ?? {};

        if (
          token === config.sdk.launchDarkly &&
          (appVersion !== VERSION || appBuildTime !== TIMESTAMP)
        ) {
          localStorage.removeItem(key);
        }
      });
  }

  /**
   * Sets up the LaunchDarkly event listeners.
   *
   * @param options The listener options.
   */
  private setupClientEventListeners(options: {
    /** Whether or not to validate the flags. */
    validate: boolean;
    /** The resolve executor function to signal readiness. */
    resolve: (arg: InitResolvedValue) => void;
  }): void {
    if (this.client) {
      // The ready is event will always trigger regardless of whether it
      // successfully connected to LaunchDarkly.
      this.client.on('ready', () => {
        if (options.validate) {
          this.validateFlags();
        }

        this.flags = { ...defaultFeatureFlags, ...this.client?.allFlags() };
        options.resolve({ flags: this.flags });

        this.client?.on('change', () => {
          // Because we've set bootstrap to localStorage, LaunchDarkly will
          // attempt to fetch the flags from persistent storage in order to make
          // them immediately available on page load. Meanwhile, after the ready
          // event, the client makes a background request to LaunchDarkly to fetch
          // the latest values and will subsequently store them in localStorage.
          // We subscribe to the change event with a noop so that LaunchDarkly
          // always syncs localStorage with the latest values behind the scenes
          // while the application is running. Thus when the browser is reloaded
          // it will always be bootstrapped with the most recent set of flags.
        });
      });
    }
  }

  /**
   * Warns about LaunchDarkly not being able to fetch the flags and against
   * discrepancies between the server flags and the default flags.
   */
  private validateFlags(): void {
    if (this.client) {
      const flags = this.client.allFlags();

      if (!Object.keys(flags).length) {
        console.warn(
          'There was an error connecting to LaunchDarkly. Falling back to default flags.',
          defaultFeatureFlags,
        );
      } else {
        const missingDefaultFlags = Object.keys(flags).filter(
          (flag) => typeof defaultFeatureFlags[flag] === 'undefined',
        );
        const missingLaunchDarklyFlags = Object.keys(
          defaultFeatureFlags,
        ).filter((flag) => typeof flags[flag] === 'undefined');

        const isDevelopment = process.env.NODE_ENV === 'development';

        if (
          isDevelopment &&
          (missingDefaultFlags.length || missingLaunchDarklyFlags.length)
        ) {
          console.warn(
            `There is a discrepancy between the LaunchDarkly and default feature flags.\n`,
            missingDefaultFlags.length
              ? `- LaunchDarkly has flags without defaults:\n\t${missingDefaultFlags.join(
                  ',\n\t',
                )}\n`
              : '',
            missingLaunchDarklyFlags.length
              ? `- The fallback has flags not in LaunchDarkly:\n\t${missingLaunchDarklyFlags.join(
                  ',\n\t',
                )}`
              : '',
          );
        }
      }
    }
  }
}

export const featureFlags = new FeatureFlags();
